import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function AirbnbTokyoGuidePage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);

    const title = t('seo.airbnbGuide.title');
    const description = t('seo.airbnbGuide.description');
    const keywords = t('seo.airbnbGuide.keywords');

    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="keywords"]', 'content', keywords);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', `${SITE_URL}/airbnb-tokyo-legal-guide`);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('link[rel="canonical"]', 'href', `${SITE_URL}/airbnb-tokyo-legal-guide`);
    setMeta('meta[name="last-modified"]', 'content', '2025-07-14');

    const schemaId = 'schema-airbnb-tokyo-guide';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: description,
      url: `${SITE_URL}/airbnb-tokyo-legal-guide`,
      datePublished: '2026-01-01',
      dateModified: '2025-07-14',
      author: {
        '@type': 'Organization',
        name: 'Magome Kominka',
        url: `${SITE_URL}/`,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Magome Kominka',
        url: `${SITE_URL}/`,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/airbnb-tokyo-legal-guide`,
      },
      about: [
        { '@type': 'Thing', name: 'Airbnb Tokyo License' },
        { '@type': 'Thing', name: 'Minpaku Registration Tokyo' },
        { '@type': 'Thing', name: 'Ryokan License Tokyo' },
        { '@type': 'Thing', name: 'Airbnb Management Tokyo' },
      ],
    });

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [i18n.language, t]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Serif JP', serif" }}>
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-36 pb-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a0a00 0%, #3d1a00 40%, #5c2a00 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/125a3c72af882e0a47e24dbcc2f1f236.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#C8960C]/20 border border-[#C8960C]/40 text-[#C8960C] text-xs font-semibold px-4 py-1.5 rounded-full mb-6 tracking-widest uppercase">
            <i className="ri-shield-check-line"></i>
            Complete Legal Guide · 2026 Edition
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            How to Start Airbnb in Tokyo Legally
            <span className="block text-[#C8960C] mt-2 text-3xl md:text-4xl">
              Minpaku &amp; Ryokan License Guide
            </span>
          </h1>
          <p className="text-lg text-white/80 max-w-3xl mx-auto leading-relaxed mb-8">
            A comprehensive, experience-based guide to legally launching your short-term rental in Tokyo —
            covering <strong className="text-white">Airbnb Tokyo license</strong> requirements,{' '}
            <strong className="text-white">minpaku registration Tokyo</strong> steps, cost estimates, and
            when to hire professional support.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-white/70">
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-[#C8960C]"></i> 8 min read
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-line text-[#C8960C]"></i> Tokyo · Ota City
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-[#C8960C]"></i> Updated 2026
            </span>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-10 bg-[#F5F1E8] border-b border-[#C8960C]/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-sm font-bold text-[#8B2500] uppercase tracking-widest mb-4">
            Table of Contents
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { num: '01', label: 'Legal Requirements in Tokyo', href: '#legal-requirements' },
              { num: '02', label: 'Minpaku vs Ryokan License', href: '#minpaku-vs-ryokan' },
              { num: '03', label: 'Licensing Process Overview', href: '#licensing-process' },
              { num: '04', label: 'Cost Estimate', href: '#cost-estimate' },
              { num: '05', label: 'Realistic Timeline', href: '#timeline' },
              { num: '06', label: 'When to Hire Support', href: '#hire-support' },
            ].map((item) => (
              <a
                key={item.num}
                href={item.href}
                className="flex items-center gap-3 bg-white border border-[#C8960C]/20 rounded-lg px-4 py-3 hover:border-[#8B2500] hover:shadow-sm transition-all duration-200 cursor-pointer group"
              >
                <span className="text-xs font-bold text-[#C8960C] group-hover:text-[#8B2500]">
                  {item.num}
                </span>
                <span className="text-sm text-[#3d1f0a] leading-tight">{item.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Article */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {/* ── Section 1: Legal Requirements ── */}
          <section id="legal-requirements">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white">
                <i className="ri-government-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">
                  Section 01
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-[#8B2500]">
                  Legal Requirements in Tokyo
                </h2>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5">
              <p>
                Operating a short-term rental in Tokyo without the correct license is illegal and can
                result in significant fines, forced closure, and reputational damage. Japan's regulatory
                framework for short-term rentals is governed by two primary laws, and understanding
                which applies to your situation is the essential first step before listing on any
                platform.
              </p>
              <p>
                The <strong>Housing Accommodation Business Act (住宅宿泊事業法)</strong>, commonly known as
                the Minpaku Law, came into effect in June 2018. It created a formal registration pathway
                for homeowners wishing to rent their properties to guests. Under this law, operators must
                notify their prefectural government and comply with a strict{' '}
                <strong>180-day annual operating limit</strong>. Tokyo's individual wards may impose
                additional restrictions — some limiting operations to weekends only, or prohibiting
                short-term rentals in residential zones entirely.
              </p>
              <p>
                The <strong>Hotel Business Act (旅館業法)</strong> governs the Ryokan license pathway,
                which permits year-round operation without day restrictions. This is the preferred route
                for anyone serious about <strong>Airbnb management Tokyo</strong> as a primary business.
                The Simple Lodging (簡易宿所) category under this act is the most accessible for small
                property operators and is the license type our team holds in Ota City.
              </p>
              <p>
                Beyond the national framework, Tokyo's 23 wards each have their own supplementary
                regulations. Ota City (大田区) is notably one of the most permissive areas in Tokyo for
                short-term rentals, having been designated a National Strategic Special Zone — which is
                why it has become a hub for legitimate{' '}
                <strong>Airbnb Tokyo license</strong> operations. Other wards such as Shinjuku and
                Shibuya impose far stricter limitations.
              </p>
              <p>
                Key legal obligations for all licensed operators include: displaying your license
                number on all listings, maintaining a guest register, providing 24/7 emergency contact
                capability, conducting identity verification for all guests, and submitting periodic
                reports to local authorities. Non-compliance with any of these requirements — even
                after obtaining a license — can result in suspension or revocation.
              </p>
            </div>

            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {[
                {
                  icon: 'ri-file-text-line',
                  title: 'Minpaku Law',
                  desc: 'Max 180 days/year. Notification-based. Ward restrictions apply.',
                },
                {
                  icon: 'ri-hotel-line',
                  title: 'Ryokan License',
                  desc: 'Year-round operation. Full licensing required. Higher revenue potential.',
                },
                {
                  icon: 'ri-map-2-line',
                  title: 'Ota City Advantage',
                  desc: 'National Strategic Special Zone. Most permissive ward in Tokyo.',
                },
              ].map((card) => (
                <div key={card.title} className="bg-[#F5F1E8] border border-[#C8960C]/30 rounded-xl p-5">
                  <div className="w-10 h-10 flex items-center justify-center mb-3">
                    <i className={`${card.icon} text-2xl text-[#8B2500]`}></i>
                  </div>
                  <h3 className="font-bold text-[#8B2500] mb-1">{card.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Section 2: Minpaku vs Ryokan ── */}
          <section id="minpaku-vs-ryokan">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white">
                <i className="ri-scales-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">
                  Section 02
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-[#8B2500]">
                  Minpaku vs Ryokan License
                </h2>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5 mb-10">
              <p>
                Choosing the right licensing path is one of the most consequential decisions in your{' '}
                <strong> minpaku registration Tokyo</strong> journey. The two paths differ
                significantly in operational freedom, revenue potential, application complexity, and
                ongoing compliance burden. Here is a detailed comparison based on our direct experience
                with both systems.
              </p>
            </div>

            {/* Comparison Table */}
            <div className="overflow-x-auto rounded-xl border border-[#C8960C]/30 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #8B2500, #C8960C)' }}>
                    <th className="text-left px-5 py-4 text-white font-semibold">Feature</th>
                    <th className="text-center px-5 py-4 text-white font-semibold">
                      Minpaku (住宅宿泊)
                    </th>
                    <th className="text-center px-5 py-4 text-white font-semibold">
                      Ryokan / Simple Lodging
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      feature: 'Operating Days',
                      minpaku: 'Max 180 days/year',
                      ryokan: '365 days/year ✓',
                      highlight: true,
                    },
                    {
                      feature: 'Application Type',
                      minpaku: 'Notification (届出)',
                      ryokan: 'Full License (許可)',
                    },
                    {
                      feature: 'Processing Time',
                      minpaku: '2–4 weeks',
                      ryokan: '2–4 months',
                      highlight: true,
                    },
                    {
                      feature: 'Application Fee',
                      minpaku: '~¥0–5,000',
                      ryokan: '~¥22,000–55,000',
                    },
                    {
                      feature: 'Fire Safety Requirements',
                      minpaku: 'Basic',
                      ryokan: 'Comprehensive',
                      highlight: true,
                    },
                    {
                      feature: 'Minimum Room Size',
                      minpaku: '3.3 m² per guest',
                      ryokan: '3.3 m² per guest',
                    },
                    {
                      feature: 'Revenue Potential',
                      minpaku: 'Limited (180-day cap)',
                      ryokan: 'Full potential ✓',
                      highlight: true,
                    },
                    {
                      feature: 'Suitable For',
                      minpaku: 'Side income / casual use',
                      ryokan: 'Professional business ✓',
                    },
                    {
                      feature: 'Ward Restrictions',
                      minpaku: 'Varies — often strict',
                      ryokan: 'Less restricted',
                      highlight: true,
                    },
                    {
                      feature: 'Management Viability',
                      minpaku: 'Difficult to outsource',
                      ryokan: 'Fully manageable ✓',
                    },
                  ].map((row, i) => (
                    <tr
                      key={row.feature}
                      className={`border-t border-[#C8960C]/10 ${
                        row.highlight ? 'bg-[#FFF9F0]' : i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF8]'
                      }`}
                    >
                      <td className="px-5 py-3.5 font-medium text-[#3d1f0a]">{row.feature}</td>
                      <td className="px-5 py-3.5 text-center text-gray-600">{row.minpaku}</td>
                      <td className="px-5 py-3.5 text-center font-semibold text-[#8B2500]">{row.ryokan}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-8 bg-[#FFF9E6] border-l-4 border-[#C8960C] p-6 rounded-r-xl">
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-[#8B2500]">Our Recommendation:</strong> If you are serious about
                building a profitable short-term rental business in Tokyo, pursue the{' '}
                <strong>Ryokan (Simple Lodging) license</strong>. The 180-day cap under Minpaku makes it
                nearly impossible to generate meaningful income or justify professional{' '}
                <strong>Airbnb management Tokyo</strong> services. Our team operates under a Ryokan
                license in Ota City — and the difference in revenue potential is substantial.
              </p>
            </div>
          </section>

          {/* ── Section 3: Licensing Process ── */}
          <section id="licensing-process">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white">
                <i className="ri-route-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">
                  Section 03
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-[#8B2500]">
                  Licensing Process Overview
                </h2>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5 mb-10">
              <p>
                The <strong>ryokan license application Tokyo</strong> process involves multiple government
                agencies and requires careful coordination. Below is the step-by-step process our team has
                navigated firsthand in Ota City. While specific requirements vary slightly by ward, this
                framework applies broadly across Tokyo.
              </p>
            </div>

            <div className="space-y-5">
              {[
                {
                  step: '01',
                  title: 'Property Feasibility Assessment',
                  desc: 'Verify zoning classification, building use category, and condominium bylaws. Confirm the property is legally eligible for short-term rental use. This step prevents wasted investment on ineligible properties.',
                  icon: 'ri-search-eye-line',
                },
                {
                  step: '02',
                  title: 'Pre-Application Consultation with Ward Office',
                  desc: "Schedule an informal meeting with your local ward's health department (保健所). Officials will review your property details and provide specific guidance. This single step prevents the majority of application rejections.",
                  icon: 'ri-building-2-line',
                },
                {
                  step: '03',
                  title: 'Fire Safety Compliance & Inspection',
                  desc: 'Hire a certified fire safety inspector to assess your property. Install required equipment: smoke detectors, fire extinguishers, emergency lighting, and evacuation signage. Obtain the official fire safety certificate.',
                  icon: 'ri-fire-line',
                },
                {
                  step: '04',
                  title: 'Document Preparation',
                  desc: 'Compile all required documents: building registry (登記簿謄本), floor plans, fire safety certificate, operator identification, management agreements, and emergency response procedures — all in Japanese.',
                  icon: 'ri-file-list-3-line',
                },
                {
                  step: '05',
                  title: 'Formal Application Submission',
                  desc: 'Submit the complete application package to the ward health department. Pay the application fee (¥22,000–55,000 depending on ward). Receive your application reference number and inspection scheduling.',
                  icon: 'ri-send-plane-line',
                },
                {
                  step: '06',
                  title: 'On-Site Inspection',
                  desc: 'Health department and fire safety officials conduct an on-site inspection. They verify facility compliance, safety equipment, and operational readiness. Address any deficiencies immediately to avoid re-inspection delays.',
                  icon: 'ri-eye-line',
                },
                {
                  step: '07',
                  title: 'License Issuance & Platform Registration',
                  desc: 'Receive your official Ryokan license. Register your license number on Airbnb, Booking.com, and other platforms. Display the license certificate prominently at the property. Begin operations.',
                  icon: 'ri-award-line',
                },
              ].map((item, i) => (
                <div
                  key={item.step}
                  className="flex gap-5 bg-white border border-[#C8960C]/20 rounded-xl p-6 hover:border-[#8B2500] hover:shadow-md transition-all duration-300"
                >
                  <div className="flex-shrink-0 flex flex-col items-center">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#8B2500] to-[#C8960C] rounded-full text-white font-bold text-sm">
                      {item.step}
                    </div>
                    {i < 6 && <div className="w-0.5 flex-1 mt-3 bg-[#C8960C]/20 min-h-6" />}
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className={`${item.icon} text-[#C8960C] text-lg`}></i>
                      </div>
                      <h3 className="font-bold text-[#8B2500] text-lg">{item.title}</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/minpaku-registration-tokyo"
                className="inline-flex items-center gap-2 text-[#8B2500] font-semibold hover:underline cursor-pointer"
              >
                <i className="ri-arrow-right-circle-line text-xl"></i>
                Read our detailed step-by-step Minpaku registration guide →
              </Link>
            </div>
          </section>

          {/* ── Section 4: Cost Estimate ── */}
          <section id="cost-estimate">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white">
                <i className="ri-money-yen-circle-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">
                  Section 04
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-[#8B2500]">Cost Estimate</h2>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5 mb-10">
              <p>
                One of the most common questions we receive from prospective operators is: "How much
                does it cost to get an <strong>Airbnb Tokyo license</strong>?" The honest answer is
                that costs vary significantly based on property condition, ward, and whether you pursue
                the Minpaku or Ryokan path. Below are realistic estimates based on our direct experience
                with{' '}
                <strong>Ota City short-term rental support</strong>.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Minpaku Costs */}
              <div className="bg-white border-2 border-[#C8960C]/30 rounded-xl overflow-hidden">
                <div className="bg-[#C8960C]/10 px-6 py-4 border-b border-[#C8960C]/20">
                  <h3 className="font-bold text-[#8B2500] text-lg">Minpaku Registration Costs</h3>
                  <p className="text-sm text-gray-500">Notification-based · 180-day limit</p>
                </div>
                <div className="p-6 space-y-3">
                  {[
                    { item: 'Application fee', cost: '¥0 – ¥5,000' },
                    { item: 'Fire safety equipment', cost: '¥30,000 – ¥80,000' },
                    { item: 'Floor plan preparation', cost: '¥20,000 – ¥50,000' },
                    { item: 'Translation / admin support', cost: '¥30,000 – ¥80,000' },
                    { item: 'Property modifications', cost: '¥0 – ¥200,000' },
                  ].map((row) => (
                    <div
                      key={row.item}
                      className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                    >
                      <span className="text-sm text-gray-600">{row.item}</span>
                      <span className="text-sm font-semibold text-[#3d1f0a]">{row.cost}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-[#C8960C]/30">
                    <span className="font-bold text-[#8B2500]">Estimated Total</span>
                    <span className="font-bold text-[#8B2500] text-lg">¥80,000 – ¥415,000</span>
                  </div>
                </div>
              </div>

              {/* Ryokan Costs */}
              <div className="bg-white border-2 border-[#8B2500]/40 rounded-xl overflow-hidden shadow-md">
                <div
                  className="px-6 py-4 border-b border-[#8B2500]/20"
                  style={{ background: 'linear-gradient(135deg, #8B2500, #C8960C)' }}
                >
                  <h3 className="font-bold text-white text-lg">Ryokan License Costs</h3>
                  <p className="text-sm text-white/70">Full license · Year-round operation</p>
                </div>
                <div className="p-6 space-y-3">
                  {[
                    { item: 'Application fee', cost: '¥22,000 – ¥55,000' },
                    { item: 'Fire safety inspection + equipment', cost: '¥80,000 – ¥200,000' },
                    { item: 'Professional floor plans', cost: '¥50,000 – ¥120,000' },
                    { item: 'Property compliance modifications', cost: '¥100,000 – ¥500,000' },
                    { item: 'Consulting / support fees', cost: '¥100,000 – ¥300,000' },
                    { item: 'Translation & documentation', cost: '¥30,000 – ¥80,000' },
                  ].map((row) => (
                    <div
                      key={row.item}
                      className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                    >
                      <span className="text-sm text-gray-600">{row.item}</span>
                      <span className="text-sm font-semibold text-[#3d1f0a]">{row.cost}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-[#8B2500]/30">
                    <span className="font-bold text-[#8B2500]">Estimated Total</span>
                    <span className="font-bold text-[#8B2500] text-lg">¥382,000 – ¥1,255,000</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#FFF9E6] border-l-4 border-[#C8960C] p-6 rounded-r-xl">
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-[#8B2500]">Important:</strong> These figures cover licensing
                costs only and do not include property acquisition, furnishing, photography, or
                ongoing operational costs. Budget an additional ¥500,000–¥1,500,000 for a fully guest-ready
                property. Professional support typically pays for itself within 2–3 months through faster
                approval and higher occupancy rates.
              </p>
            </div>
          </section>

          {/* ── Section 5: Timeline ── */}
          <section id="timeline">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white">
                <i className="ri-calendar-check-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">
                  Section 05
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-[#8B2500]">Realistic Timeline</h2>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5 mb-10">
              <p>
                Understanding the realistic timeline for <strong>minpaku registration Tokyo</strong>{' '}
                is critical for financial planning. Many first-time applicants significantly underestimate
                the time required, leading to cash flow problems and missed peak seasons. Here is an honest
                breakdown based on our experience providing{' '}
                <strong>Tokyo Airbnb support</strong> in Ota City.
              </p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#8B2500] to-[#C8960C] hidden md:block" />

              <div className="space-y-6">
                {[
                  {
                    period: 'Week 1–2',
                    title: 'Feasibility & Consultation',
                    desc: 'Property assessment, licensing path decision, ward office pre-consultation. Identifies blockers before any investment.',
                    status: 'start',
                  },
                  {
                    period: 'Week 3–6',
                    title: 'Document Preparation',
                    desc: 'Gathering building registry, commissioning floor plans, obtaining fire safety inspection, drafting operational procedures.',
                    status: 'mid',
                  },
                  {
                    period: 'Week 7–8',
                    title: 'Facility Compliance',
                    desc: 'Installing safety equipment, making property modifications, final pre-inspection walkthrough.',
                    status: 'mid',
                  },
                  {
                    period: 'Week 9',
                    title: 'Application Submission',
                    desc: 'Formal submission to ward health department, fee payment, inspection scheduling.',
                    status: 'mid',
                  },
                  {
                    period: 'Week 10–12',
                    title: 'Inspection & Review',
                    desc: 'On-site inspection by health and fire officials, document verification, deficiency resolution.',
                    status: 'mid',
                  },
                  {
                    period: 'Week 13+',
                    title: 'License Issued — Launch',
                    desc: 'License received, platform listings activated, first bookings accepted. Revenue generation begins.',
                    status: 'end',
                  },
                ].map((item) => (
                  <div key={item.period} className="flex gap-6 md:pl-4">
                    <div
                      className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full border-4 border-white shadow-md z-10 text-white text-xs font-bold"
                      style={{
                        background:
                          item.status === 'end'
                            ? 'linear-gradient(135deg, #8B2500, #C8960C)'
                            : '#8B2500',
                      }}
                    >
                      {item.status === 'end' ? (
                        <i className="ri-check-line text-lg"></i>
                      ) : (
                        <i className="ri-time-line text-lg"></i>
                      )}
                    </div>
                    <div className="flex-1 bg-white border border-[#C8960C]/20 rounded-xl p-5 hover:border-[#8B2500] hover:shadow-sm transition-all duration-200">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-bold bg-[#8B2500] text-white px-2.5 py-0.5 rounded-full">
                          {item.period}
                        </span>
                        <h3 className="font-bold text-[#8B2500]">{item.title}</h3>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                  <h4 className="font-bold text-green-800">With Professional Support</h4>
                </div>
                <p className="text-sm text-green-700">
                  <strong>3–4 months</strong> from initial consultation to first booking. Experienced
                  guidance prevents document resubmissions and inspection failures.
                </p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-error-warning-fill text-red-500 text-xl"></i>
                  <h4 className="font-bold text-red-800">Without Professional Support</h4>
                </div>
                <p className="text-sm text-red-700">
                  <strong>6–12 months</strong> is common for first-time applicants navigating the
                  process alone. Document rejections and re-inspections are the primary causes of delay.
                </p>
              </div>
            </div>
          </section>

          {/* ── Section 6: When to Hire Support ── */}
          <section id="hire-support">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white">
                <i className="ri-user-star-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">
                  Section 06
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-[#8B2500]">
                  When to Hire Support
                </h2>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5 mb-10">
              <p>
                Not every property owner needs professional support — but most benefit significantly from
                it. The question is not whether support adds value (it almost always does), but whether
                the specific challenges you face justify the investment. Here is an honest assessment of
                when professional <strong>Tokyo minpaku support</strong> is essential versus optional.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="bg-white border-2 border-[#8B2500]/30 rounded-xl p-6">
                <h3 className="font-bold text-[#8B2500] text-lg mb-4 flex items-center gap-2">
                  <i className="ri-alert-fill text-[#C8960C]"></i>
                  You Definitely Need Support If…
                </h3>
                <ul className="space-y-3">
                  {[
                    'You are not fluent in Japanese — all official documents must be in Japanese',
                    'You are applying for a Ryokan license for the first time',
                    'Your property is in a condominium with complex ownership structures',
                    'You want to launch within 3–4 months rather than 6–12',
                    'You plan to outsource day-to-day Airbnb management',
                    'You are unsure which license type is right for your property',
                    'You have already had an application rejected or delayed',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[#8B2500] mt-0.5 flex-shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white border-2 border-[#C8960C]/30 rounded-xl p-6">
                <h3 className="font-bold text-[#8B2500] text-lg mb-4 flex items-center gap-2">
                  <i className="ri-information-fill text-[#C8960C]"></i>
                  You May Manage Alone If…
                </h3>
                <ul className="space-y-3">
                  {[
                    'You are fluent in Japanese and familiar with bureaucratic processes',
                    'You are pursuing Minpaku (notification only) for a single property',
                    'You have prior experience with Japanese government applications',
                    'Your property is a standalone house with no condominium complications',
                    'You have ample time (6+ months) and can absorb potential delays',
                    'You plan to manage the property entirely yourself',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-line text-[#C8960C] mt-0.5 flex-shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5 mb-8">
              <p>
                What makes our <strong>Tokyo Airbnb support</strong> different from typical legal
                consultants or administrative services is that we are active operators ourselves. We
                hold a Ryokan license in Ota City, run live Airbnb listings, and have direct relationships
                with local ward officials. This means our guidance is grounded in real operational
                experience — not just theoretical knowledge of the law.
              </p>
              <p>
                We offer end-to-end support: from initial feasibility assessment and{' '}
                <strong>ryokan license application Tokyo</strong> through to ongoing{' '}
                <strong>Airbnb management Tokyo</strong> — including pricing strategy, cleaning
                coordination, guest communication, and accounting support. You can engage us for the
                licensing phase only, or for full ongoing management. See our{' '}
                <Link
                  to="/airbnb-management-tokyo"
                  className="text-[#8B2500] font-semibold hover:underline"
                >
                  pricing and service plans
                </Link>{' '}
                for details.
              </p>
            </div>

            {/* Internal link block */}
            <div className="grid md:grid-cols-3 gap-4">
              {[
                {
                  icon: 'ri-price-tag-3-line',
                  title: 'View Pricing Plans',
                  desc: 'Transparent pricing for licensing support and full management services.',
                  to: '/airbnb-management-tokyo',
                  label: 'See Pricing →',
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: 'Free Consultation',
                  desc: 'Book a free 30-minute session with our Tokyo Airbnb support team.',
                  to: '/tokyo-airbnb-support#contact',
                  label: 'Book Now →',
                },
                {
                  icon: 'ri-file-download-line',
                  title: 'Free Startup Guide',
                  desc: 'Download our 2026 Tokyo Minpaku Startup Guide before you begin.',
                  to: '/tokyo-minpaku-guide',
                  label: 'Download →',
                },
              ].map((card) => (
                <Link
                  key={card.title}
                  to={card.to}
                  className="block bg-[#F5F1E8] border border-[#C8960C]/30 rounded-xl p-5 hover:border-[#8B2500] hover:shadow-md transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center mb-3">
                    <i className={`${card.icon} text-2xl text-[#8B2500]`}></i>
                  </div>
                  <h3 className="font-bold text-[#8B2500] mb-1">{card.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">{card.desc}</p>
                  <span className="text-sm font-semibold text-[#8B2500] group-hover:underline">
                    {card.label}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* ── Final CTA ── */}
          <section>
            <div
              className="relative rounded-2xl overflow-hidden p-12 text-center text-white"
              style={{ background: 'linear-gradient(135deg, #1a0a00 0%, #8B2500 50%, #C8960C 100%)' }}
            >
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Tokyo%20city%20aerial%20view%20night%20lights%20warm%20golden%20tones%20abstract%20artistic%20minimalist&width=800&height=400&seq=seo-cta-bg-02&orientation=landscape')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div className="relative">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-white/10 rounded-full border border-white/20">
                  <i className="ri-calendar-check-line text-3xl text-[#C8960C]"></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Ready to Start Your Tokyo Airbnb Business?
                </h2>
                <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Get practical, experience-based guidance from an active Tokyo host who has navigated
                  the full licensing process in Ota City. Free 30-minute consultation — no
                  obligation.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/tokyo-airbnb-support#contact"
                    className="inline-flex items-center justify-center gap-2 bg-white text-[#8B2500] px-8 py-4 rounded-full font-bold text-base hover:bg-[#F5F1E8] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                  >
                    <i className="ri-calendar-line"></i>
                    Book Free Consultation
                  </Link>
                  <Link
                    to="/airbnb-management-tokyo"
                    className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold text-base hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-price-tag-3-line"></i>
                    View Pricing Plans
                  </Link>
                </div>
                <p className="mt-6 text-sm text-white/50">
                  Airbnb Tokyo License · Minpaku Registration Tokyo · Ryokan License Tokyo · Ota City
                  Short-Term Rental Support
                </p>
              </div>
            </div>
          </section>
        </div>
      </article>

      <Footer />
    </div>
  );
}
